import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';

/**
 * Shows a message when there is no internet connection.
 *
 * Rendered once, above the navigator, rather than per screen: being offline is a state, not an
 * event, and it used to be visible on the home screen alone. Everywhere else the only sign was the
 * error toast, which shows once per outage and then stays quiet -- so a visitor who lost signal on
 * a programme page saw an app that had simply stopped responding.
 *
 * It carries the status bar inset itself, because it now sits above the navigation bar that used
 * to carry it.
 */
export default function OfflineBanner() {
	const isConnected = useNavigationStore((state) => state.isConnected);
	const insets = useSafeAreaInsets();

	if (isConnected) return null;

	return (
		<View
			accessibilityRole="alert"
			style={[styles.banner, { paddingTop: insets.top + Dimens.padding10 }]}
			testID="offline-banner"
		>
			<Ionicons name="cloud-offline-outline" size={20} style={styles.icon} />
			<Text style={styles.text}>{Translations.textNoInternet}</Text>
		</View>
	);
}

const styles = StyleSheet.create({
	banner: {
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: Colors.orange,
		paddingHorizontal: Dimens.padding16,
		paddingVertical: Dimens.padding10,
	},
	icon: {
		color: Colors.black,
		marginRight: Dimens.margin8,
	},
	text: {
		flex: 1,
		color: Colors.black,
		fontFamily: Fonts.montserratMedium,
		fontSize: Dimens.fontSize14,
	},
});
